/**
 * Élection du certificat de cession QUI FAIT FOI.
 *
 * Retour François (mail du 17/09, GD-733-LW) : « Grosse divergence entre CGIA
 * et le formulaire SKIPCAR — CGIA voit en acheteur SKIPCAR et le formulaire
 * voit PETIT Thierry. »
 *
 * Le dossier portait TROIS certificats de cession, tous légitimes : c'est une
 * chaîne de propriété, pas un doublon.
 *   1. 23/05 MARONNIER (titulaire de la carte grise) → DEMA PRESTIGE CAR
 *   2. 26/05 DEMA PRESTIGE CAR → SKIPCAR
 *   3. 18/06 DEMA PRESTIGE CAR → PETIT Thierry   ← celle du dossier
 *
 * Le formulaire n'a qu'UN slot `cerfa_cession` et fusionnait les champs des
 * trois : le nom venait de l'une, la raison sociale d'une autre. D'où un
 * acquéreur « SKIPCAR » à l'époque du mail, et — depuis que l'arbitrage des
 * champs a changé — l'élection de la cession n°1, qui produit trois alertes
 * MAJEURES toutes fausses (acquéreur introuvable parmi les pièces d'identité,
 * mandat qui ne désigne pas SKIPCAR, chronologie du récépissé incohérente).
 * Mesure du 18/09 sur la base de prod : 155 dossiers portent plus d'une
 * cession depuis le 01/08, dont 35 désignent des acquéreurs DIFFÉRENTS.
 *
 * Règle d'élection, dans l'ordre :
 *  1. L'ACQUÉREUR d'abord : la cession qui fait foi est celle dont l'acheteur
 *     est le titulaire du Cerfa 13750. C'est le seul critère qui vaut aussi
 *     bien en parcours classique qu'en parcours A.
 *  2. À défaut, le VENDEUR : la cession dont le vendeur est le titulaire de la
 *     carte grise. Exact en parcours classique ; en parcours A elle désigne la
 *     PREMIÈRE cession de la chaîne (le pro qui achète), donc elle ne passe
 *     qu'après le critère acquéreur — jamais avant.
 *  3. À défaut, la plus RÉCENTE : une revente est toujours postérieure.
 *  4. À défaut, la meilleure confiance de type (comportement historique).
 *
 * Module PUR (aucun effet de bord, aucun accès store) → testable directement.
 */

import type { UploadedDocResult } from '../store/useFormCGIAStore';

export interface CessionPoolEntry {
  doc: UploadedDocResult;
}

/** Une partie d'une cession : un particulier (nom + prénom) ou une société. */
export interface PartieRef {
  nom: string | null;
  prenom: string | null;
  raisonSociale: string | null;
}

export interface CessionRefs {
  /** Titulaire du Cerfa 13750 = l'ACQUÉREUR du dossier. */
  acquereur: PartieRef | null;
  /** Titulaire de la carte grise = le VENDEUR sortant. */
  titulaireCarteGrise: PartieRef | null;
}

export type CessionCritere = 'acquereur_13750' | 'vendeur_carte_grise' | 'plus_recente' | 'confiance';

export interface CessionElection {
  /** La cession qui fait foi, ou null si le pool est vide. */
  elue: UploadedDocResult | null;
  /** Les autres cessions du dossier — pièces de la chaîne de propriété. */
  autres: UploadedDocResult[];
  /** Critère qui a tranché (trace IA et tests). */
  critere: CessionCritere | null;
}

const normName = (v: unknown): string =>
  String(v ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const docFieldStr = (doc: UploadedDocResult | undefined, key: string): string | null => {
  const v = (doc?.ocrResponse?.fields as Record<string, { value?: unknown }> | undefined)?.[key]?.value;
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
};

const partieFrom = (doc: UploadedDocResult | undefined, prefixe: string): PartieRef | null => {
  if (!doc) return null;
  const ref: PartieRef = {
    nom: docFieldStr(doc, `${prefixe}_nom`),
    prenom: docFieldStr(doc, `${prefixe}_prenom`),
    raisonSociale: docFieldStr(doc, `${prefixe}_raison_sociale`),
  };
  return ref.nom || ref.prenom || ref.raisonSociale ? ref : null;
};

/**
 * Références d'identité des deux bouts du dossier, lues sur les pièces qui
 * font foi : le 13750 pour l'acquéreur, la carte grise pour le vendeur.
 */
export const buildCessionRefs = (docs: Partial<Record<string, UploadedDocResult>>): CessionRefs => ({
  acquereur: partieFrom(docs.cerfa_demande_immat, 'titulaire'),
  titulaireCarteGrise: partieFrom(docs.carte_grise, 'titulaire'),
});

/**
 * Deux parties désignent-elles la même personne (ou la même société) ?
 *
 * Une société se reconnaît à sa raison sociale, un particulier à son couple
 * nom + prénom. Le NOM SEUL ne suffit pas : sur une chaîne de revente, le même
 * professionnel apparaît des deux côtés (DEMA PRESTIGE CAR vend ET achète), et
 * dans une famille le nom est commun — c'est exactement l'erreur que le
 * routage des pièces d'identité a dû corriger le 18/09.
 */
export const memePartie = (a: PartieRef | null, b: PartieRef | null): boolean => {
  if (!a || !b) return false;
  if (a.raisonSociale && b.raisonSociale && normName(a.raisonSociale) === normName(b.raisonSociale)) return true;
  if (a.nom && b.nom && a.prenom && b.prenom) {
    return normName(a.nom) === normName(b.nom) && normName(a.prenom) === normName(b.prenom);
  }
  return false;
};

/** Date de vente exploitable (AAAA-MM-JJ), ou null. */
const dateVenteOf = (doc: UploadedDocResult): string | null => {
  const v = docFieldStr(doc, 'date_vente');
  return v && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : null;
};

const confidenceOf = (doc: UploadedDocResult): number => doc.ocrResponse?.docTypeMatchConfidence ?? 0;

/** La plus récente par date de vente ; à date égale ou absente, la mieux notée. */
const plusRecente = (docs: readonly UploadedDocResult[]): UploadedDocResult =>
  [...docs].sort((a, b) => {
    const da = dateVenteOf(a) ?? '';
    const db = dateVenteOf(b) ?? '';
    if (da !== db) return db.localeCompare(da);
    return confidenceOf(b) - confidenceOf(a);
  })[0];

/**
 * Élit la cession qui fait foi parmi celles du dossier. Recalculé
 * intégralement à chaque appel : le résultat ne dépend que du contenu du pool
 * et des pièces de référence, jamais de l'ordre d'arrivée — donc l'arrivée
 * tardive du 13750 ou de la carte grise re-tranche l'élection.
 */
export const elireCession = (pool: readonly CessionPoolEntry[], refs: CessionRefs): CessionElection => {
  const docs = pool.map((e) => e.doc).filter((d): d is UploadedDocResult => Boolean(d));
  if (docs.length === 0) return { elue: null, autres: [], critere: null };
  if (docs.length === 1) return { elue: docs[0], autres: [], critere: 'confiance' };

  const finir = (elue: UploadedDocResult, critere: CessionCritere): CessionElection => ({
    elue,
    autres: docs.filter((d) => d !== elue),
    critere,
  });

  // 1. L'acheteur de la cession est le titulaire du 13750.
  const parAcquereur = docs.filter((d) => memePartie(partieFrom(d, 'acheteur'), refs.acquereur));
  if (parAcquereur.length === 1) return finir(parAcquereur[0], 'acquereur_13750');
  // Plusieurs cessions au même acquéreur (un exemplaire re-scanné, une vente
  // corrigée) : la plus récente tranche, puis la confiance.
  if (parAcquereur.length > 1) return finir(plusRecente(parAcquereur), 'acquereur_13750');

  // 2. Le vendeur de la cession est le titulaire de la carte grise.
  const parVendeur = docs.filter((d) => memePartie(partieFrom(d, 'vendeur'), refs.titulaireCarteGrise));
  if (parVendeur.length === 1) return finir(parVendeur[0], 'vendeur_carte_grise');
  if (parVendeur.length > 1) return finir(plusRecente(parVendeur), 'vendeur_carte_grise');

  // 3. La plus récente.
  const datees = docs.filter((d) => dateVenteOf(d));
  if (datees.length > 0) return finir(plusRecente(datees), 'plus_recente');

  // 4. La meilleure confiance de type.
  return finir([...docs].sort((a, b) => confidenceOf(b) - confidenceOf(a))[0], 'confiance');
};
